// src/Message/index.tsx
import ConfigProvider, { globalConfig } from "antd/es/config-provider";
import { render } from "rc-util/lib/React/render";
import * as React from "react";
import PurePanel from "./PurePanel";
import useMessage, { useInternalMessage } from "./useMessage";
import { wrapPromiseFn } from "./util";
var methods = [
  "success",
  "info",
  "warning",
  "error",
  "loading"
];
var message = null;
var act = (callback) => callback();
var taskQueue = [];
var defaultGlobalConfig = {};
function getGlobalContext() {
  const {
    prefixCls: globalPrefixCls,
    getContainer: globalGetContainer,
    duration,
    rtl,
    maxCount,
    top
  } = defaultGlobalConfig;
  const mergedPrefixCls = globalPrefixCls != null ? globalPrefixCls : globalConfig().getPrefixCls("message");
  const mergedContainer = (globalGetContainer == null ? void 0 : globalGetContainer()) || document.body;
  return {
    prefixCls: mergedPrefixCls,
    container: mergedContainer,
    duration,
    rtl,
    maxCount,
    top
  };
}
var GlobalHolder = React.forwardRef((_, ref) => {
  const initializeMessageConfig = () => {
    const { prefixCls, container, maxCount, duration, rtl, top } = getGlobalContext();
    return {
      prefixCls,
      getContainer: () => container,
      maxCount,
      duration,
      rtl,
      top
    };
  };
  const [messageConfig, setMessageConfig] = React.useState(
    initializeMessageConfig
  );
  const [api, holder] = useInternalMessage(messageConfig);
  const global = globalConfig();
  const rootPrefixCls = global.getRootPrefixCls();
  const rootIconPrefixCls = global.getIconPrefixCls();
  const sync = () => {
    setMessageConfig(initializeMessageConfig);
  };
  React.useEffect(sync, []);
  React.useImperativeHandle(ref, () => {
    const instance = { ...api };
    Object.keys(instance).forEach((method) => {
      instance[method] = (...args) => {
        sync();
        return api[method](...args);
      };
    });
    return {
      instance,
      sync
    };
  });
  return /* @__PURE__ */ React.createElement(ConfigProvider, { prefixCls: rootPrefixCls, iconPrefixCls: rootIconPrefixCls }, holder);
});
function flushNotice() {
  if (!message) {
    const holderFragment = document.createDocumentFragment();
    const newMessage = {
      fragment: holderFragment
    };
    message = newMessage;
    act(() => {
      render(
        /* @__PURE__ */ React.createElement(
          GlobalHolder,
          {
            ref: (node) => {
              const { instance, sync } = node || {};
              Promise.resolve().then(() => {
                if (!newMessage.instance && instance) {
                  newMessage.instance = instance;
                  newMessage.sync = sync;
                  flushNotice();
                }
              });
            }
          }
        ),
        holderFragment
      );
    });
    return;
  }
  if (!message.instance) {
    return;
  }
  taskQueue.forEach((task) => {
    const { type, skipped } = task;
    if (!skipped) {
      switch (type) {
        case "open": {
          act(() => {
            const closeFn = message.instance.open({
              ...defaultGlobalConfig,
              ...task.config
            });
            closeFn == null ? void 0 : closeFn.then(task.resolve);
            task.setCloseFn(closeFn);
          });
          break;
        }
        case "destroy":
          act(() => {
            message == null ? void 0 : message.instance.destroy(task.key);
          });
          break;
        default: {
          act(() => {
            const closeFn = message.instance[type](...task.args);
            closeFn == null ? void 0 : closeFn.then(task.resolve);
            task.setCloseFn(closeFn);
          });
        }
      }
    }
  });
  taskQueue = [];
}
function setMessageGlobalConfig(config) {
  defaultGlobalConfig = {
    ...defaultGlobalConfig,
    ...config
  };
  act(() => {
    var _a;
    (_a = message == null ? void 0 : message.sync) == null ? void 0 : _a.call(message);
  });
}
function open(config) {
  const result = wrapPromiseFn((resolve) => {
    let closeFn;
    const task = {
      type: "open",
      config,
      resolve,
      setCloseFn: (fn) => {
        closeFn = fn;
      }
    };
    taskQueue.push(task);
    return () => {
      if (closeFn) {
        act(() => {
          closeFn();
        });
      } else {
        task.skipped = true;
      }
    };
  });
  flushNotice();
  return result;
}
function typeOpen(type, args) {
  const result = wrapPromiseFn((resolve) => {
    let closeFn;
    const task = {
      type,
      args,
      resolve,
      setCloseFn: (fn) => {
        closeFn = fn;
      }
    };
    taskQueue.push(task);
    return () => {
      if (closeFn) {
        act(() => {
          closeFn();
        });
      } else {
        task.skipped = true;
      }
    };
  });
  flushNotice();
  return result;
}
function destroy(key) {
  taskQueue.push({
    type: "destroy",
    key
  });
  flushNotice();
}
var baseStaticMethods = {
  open,
  destroy,
  config: setMessageGlobalConfig,
  useMessage,
  _InternalPanelDoNotUseOrYouWillBeFired: PurePanel
};
var staticMethods = baseStaticMethods;
methods.forEach((type) => {
  staticMethods[type] = (...args) => typeOpen(type, args);
});
var noop = () => {
};
var actWrapper = noop;
if (false) {
  actWrapper = (wrapper) => {
    act = wrapper;
  };
}
var actDestroy = noop;
if (false) {
  actDestroy = () => {
    message = null;
  };
}
var Message_default = staticMethods;
export {
  actDestroy,
  actWrapper,
  Message_default as default
};
